// @givmo/app-admin — Remove admin confirmation dialog, three modes (07-1.5b).
//
// Ports the Flutter `RemoveAdminPopUp`. The mode is computed by the caller (the page) BEFORE opening
// and passed in as a `RemoveTarget`:
//   - BLOCKED (last-Owner safeguard): heading "Unable to delete admin. There must always be at least 1
//     admin account with the Global Admin role." (the whole-company role's name on this page, D-EP-76
//     (3)) + a single "Cancel" button that just closes. Issues NO DELETE. (NB: the Flutter source has a
//     stray leading `"` typo before "There must…" — FIXED here.)
//   - SELF: heading "Remove yourself as an admin?", the name, the consequence line
//     "You'll lose access to the admin portal and be signed out immediately.", button "Remove and sign
//     out". On success the page signs the user out + navigates to /login-home.
//   - NORMAL: heading "Are you sure you want to remove this admin?", the name, button "Remove".
//
// The DELETE itself (`companiesRemoveAdminFromCompany`) is owned by the page so it can branch
// self-removal (signOut + navigate) vs normal (invalidate + toast) on success. This dialog is pure
// presentation + the confirm callback.
//
// Drawn in the house's confirm-dialog register (EP-WEB44), as the invite/edit dialog beside it and
// the shared `ConfirmDialog`: the dialog title, the SaaS secondary Cancel and danger confirm.

import { Button, Dialog, testIdProp } from '@givmo/design-system';
import { GLOBAL_ADMIN_LABEL } from '@givmo/org-console';

export type RemoveMode = 'blocked' | 'self' | 'normal';

export interface AdminRemoveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: RemoveMode;
  /** The target admin's display name (shown as the subject). */
  name: string;
  loading: boolean;
  /** Fired only in 'self'/'normal' mode (blocked mode's button just closes). */
  onConfirm: () => void;
}

const BLOCKED_HEADING = `Unable to delete admin. There must always be at least 1 admin account with the ${GLOBAL_ADMIN_LABEL} role.`;

export function AdminRemoveDialog({
  open,
  onOpenChange,
  mode,
  name,
  loading,
  onConfirm,
}: AdminRemoveDialogProps) {
  const blocked = mode === 'blocked';
  const isSelf = mode === 'self';

  const heading = blocked
    ? BLOCKED_HEADING
    : isSelf
      ? 'Remove yourself as an admin?'
      : 'Are you sure you want to remove this admin?';

  const confirmLabel = blocked ? 'Cancel' : isSelf ? 'Remove and sign out' : 'Remove';

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <Dialog.Content
        aria-label={blocked ? 'Unable to delete admin' : 'Remove admin'}
        className="flex flex-col gap-12"
        testId="admins-remove-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>

        {!blocked && (
          <p
            className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
            {...testIdProp('admins-remove-dialog-subject')}
          >
            {name}
          </p>
        )}

        {isSelf && (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp('admins-remove-dialog-consequence')}
          >
            You&apos;ll lose access to the admin portal and be signed out immediately.
          </p>
        )}

        <div className="mt-12 flex items-center justify-end gap-8">
          {blocked ? (
            <Dialog.Close
              render={<Button appearance="saas" tone="secondary" size="md" />}
              {...testIdProp('admins-remove-dialog-cancel')}
            >
              Cancel
            </Dialog.Close>
          ) : (
            <>
              <Dialog.Close
                disabled={loading}
                render={<Button appearance="saas" tone="secondary" size="md" />}
                {...testIdProp('admins-remove-dialog-cancel')}
              >
                Cancel
              </Dialog.Close>
              <Button
                appearance="saas"
                tone="danger"
                size="md"
                loading={loading}
                onClick={onConfirm}
                testId="admins-remove-dialog-confirm"
              >
                {confirmLabel}
              </Button>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
